import React, { createContext, useContext, useState, ReactNode } from 'react';
import { Snackbar, Alert, AlertColor } from '@mui/material';

type SnackbarContextType = {
	showSnackbar: (message: string, severity?: AlertColor, duration?: number) => void;
};

const SnackbarContext = createContext<SnackbarContextType | undefined>(undefined);

export const useSnackbar = (): SnackbarContextType => {
	const context = useContext(SnackbarContext);
	if (!context) {
		throw new Error('useSnackbar must be used within a SnackbarProvider');
	}
	return context;
};

export const SnackbarProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
	const [open, setOpen] = useState(false);
	const [duration, setDuration] = useState(6000);
	const [message, setMessage] = useState('');
	const [severity, setSeverity] = useState<AlertColor>('success');

	const showSnackbar = (msg: string, sev: AlertColor = 'success', duration = 6000) => {
		setMessage(msg);
		setSeverity(sev);
		setOpen(true);
		setDuration(duration);
	};

	const handleClose = () => {
		setOpen(false);
	};

	return (
		<SnackbarContext.Provider value={{ showSnackbar }}>
			{children}
			<Snackbar
				open={open}
				autoHideDuration={duration}
				onClose={handleClose}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
			>
				<Alert onClose={handleClose} severity={severity} elevation={6} variant="filled">
					{message}
				</Alert>
			</Snackbar>
		</SnackbarContext.Provider>
	);
};
